<template>
  <!-- 模块字典管理：仅管理本模块(前缀隔离)的字典类型与字典数据 -->
  <ContentWrap>
    <el-alert
      v-if="typePrefix === 'food_'"
      title="餐饮字典用于配置本模块下拉选项与状态文案（如订单状态、配送方式、券类型等）。字典类型请以 food_ 开头，与系统全局字典隔离；修改后请刷新页面或重新打开相关表单生效。"
      type="info"
      :closable="false"
      show-icon
      class="mb-12px"
    />
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="68px"
    >
      <el-form-item label="字典名称" prop="name">
        <el-input
          v-model="queryParams.name"
          class="!w-240px"
          clearable
          placeholder="请输入字典名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="字典类型" prop="type">
        <el-input
          v-model="queryParams.type"
          class="!w-240px"
          clearable
          :placeholder="`请输入字典类型(默认${typePrefix}前缀)`"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select
          v-model="queryParams.status"
          class="!w-240px"
          clearable
          placeholder="请选择字典状态"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['system:dict:create']"
          plain
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['system:dict:delete']"
          :disabled="checkedIds.length === 0"
          plain
          type="danger"
          @click="handleDeleteBatch"
        >
          <Icon class="mr-5px" icon="ep:delete" />
          批量删除
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" @selection-change="handleRowCheckboxChange">
      <el-table-column type="selection" width="55" />
      <el-table-column align="center" label="字典编号" prop="id" />
      <el-table-column align="center" label="字典名称" prop="name" show-overflow-tooltip />
      <el-table-column align="center" label="字典类型" prop="type" width="300" />
      <el-table-column align="center" label="状态" prop="status">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="scope.row.status" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="备注" min-width="140" prop="remark" show-overflow-tooltip />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
      />
      <el-table-column align="center" label="操作" width="170" fixed="right">
        <template #default="scope">
          <el-button
            v-hasPermi="['system:dict:update']"
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
          >
            修改
          </el-button>
          <router-link :to="'/dict/type/data/' + scope.row.type">
            <el-button link type="primary">数据</el-button>
          </router-link>
          <el-button
            v-hasPermi="['system:dict:delete']"
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <Dialog v-model="dialogVisible" :title="dialogTitle">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="80px"
    >
      <el-form-item label="字典名称" prop="name">
        <el-input v-model="formData.name" placeholder="请输入字典名称" />
      </el-form-item>
      <el-form-item label="字典类型" prop="type">
        <el-input
          v-model="formData.type"
          :disabled="typeof formData.id !== 'undefined'"
          :placeholder="`请输入字典类型，自动添加${typePrefix}前缀`"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input v-model="formData.remark" placeholder="请输入内容" type="textarea" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import * as DictTypeApi from '@/api/system/dict/dict.type'
import { CommonStatusEnum } from '@/utils/constants'

defineOptions({ name: 'ModuleDictType' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化
const route = useRoute()

// 模块识别：meta.query.module / meta.module / 路径推断（scenicarea→scenic，避免被 Boss 圈同名路由覆盖后丢前缀）
const resolveModule = (value: unknown): string => {
  if (Array.isArray(value)) return String(value[0] || '')
  return String(value || '')
}
const inferModuleFromPath = (path: string): string => {
  const p = (path || '').toLowerCase()
  if (p.includes('/scenicarea') || p.includes('/scenic/')) return 'scenic'
  if (p.includes('/bosscircle') || p.includes('/business/')) return 'bosscircle'
  if (p.includes('/matchmaker') || p.includes('/love')) return 'matchmaker'
  if (p.includes('/microscene')) return 'microscene'
  if (p.includes('/survey')) return 'survey'
  if (p.includes('/food') || p.includes('/repast') || p.includes('/catering')) return 'food'
  if (p.includes('/fin')) return 'fin'
  return ''
}
const moduleKey = computed(() => {
  const fromMeta =
    resolveModule(route.meta?.query?.module) ||
    resolveModule((route.query as any)?.module) ||
    resolveModule(route.meta?.module) ||
    resolveModule(route.meta?.moduleCode) ||
    ''
  return fromMeta || inferModuleFromPath(route.path)
})

/** 模块字典前缀约定：景区 scenic_ / 餐饮 food_ / 融资 fin_ / 问卷 survey_ / 微场景 microscene_ / AI ai_ / 婚恋 matchmaker_ / Boss圈 bosscircle_ */
const MODULE_PREFIX: Record<string, { label: string; prefix: string }> = {
  scenic: { label: '景区', prefix: 'scenic_' },
  scenicarea: { label: '景区', prefix: 'scenic_' },
  food: { label: '餐饮', prefix: 'food_' },
  repast: { label: '餐饮', prefix: 'food_' },
  catering: { label: '餐饮', prefix: 'food_' },
  fin: { label: '融资', prefix: 'fin_' },
  survey: { label: '问卷', prefix: 'survey_' },
  microscene: { label: '微场景', prefix: 'microscene_' },
  ai: { label: 'AI', prefix: 'ai_' },
  matchmaker: { label: '婚恋', prefix: 'matchmaker_' },
  love: { label: '婚恋', prefix: 'matchmaker_' },
  bosscircle: { label: 'Boss圈', prefix: 'bosscircle_' },
  boss: { label: 'Boss圈', prefix: 'bosscircle_' }
}
const moduleConfig = computed(() => MODULE_PREFIX[moduleKey.value] || null)
const typePrefix = computed(() => moduleConfig.value?.prefix || '')
const moduleLabel = computed(() => moduleConfig.value?.label || '本模块')

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 字典表格数据
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: '',
  type: '',
  status: undefined,
  createTime: []
})
const queryFormRef = ref() // 搜索的表单

/** 查询字典类型列表 */
const getList = async () => {
  loading.value = true
  try {
    // 确保按模块前缀过滤（避免被覆盖路由后 type 为空查全量）
    if (!queryParams.type && typePrefix.value) {
      queryParams.type = typePrefix.value
    }
    const data = await DictTypeApi.getDictTypePage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.type = typePrefix.value
  handleQuery()
}

// ---------- 表单 ----------
const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const formData = ref<DictTypeApi.DictTypeVO>({
  id: undefined,
  name: '',
  type: '',
  status: CommonStatusEnum.ENABLE,
  remark: ''
})
const formRules = reactive({
  name: [{ required: true, message: '字典名称不能为空', trigger: 'blur' }],
  type: [{ required: true, message: '字典类型不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
})
const formRef = ref() // 表单 Ref

/** 打开弹窗 */
const openForm = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = `${moduleLabel.value}${t('action.' + type)}字典`
  formType.value = type
  resetForm()
  // 修改时，设置数据
  if (id) {
    formLoading.value = true
    try {
      formData.value = await DictTypeApi.getDictType(id)
    } finally {
      formLoading.value = false
    }
  }
}

/** 提交表单 */
const submitForm = async () => {
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  // 新增时自动补充模块前缀，确保字典归属本模块
  if (formType.value === 'create' && typePrefix.value) {
    const rawType = String(formData.value.type || '').trim()
    if (!rawType.startsWith(typePrefix.value)) {
      formData.value.type = `${typePrefix.value}${rawType}`
    }
  }
  formLoading.value = true
  try {
    if (formType.value === 'create') {
      await DictTypeApi.createDictType(formData.value)
      message.success(t('common.createSuccess'))
    } else {
      await DictTypeApi.updateDictType(formData.value)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    type: '',
    name: '',
    status: CommonStatusEnum.ENABLE,
    remark: ''
  }
  formRef.value?.resetFields()
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    await message.delConfirm()
    await DictTypeApi.deleteDictType(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

/** 批量删除按钮操作 */
const checkedIds = ref<number[]>([])
const handleRowCheckboxChange = (rows: DictTypeApi.DictTypeVO[]) => {
  checkedIds.value = rows.map((row) => row.id!)
}

const handleDeleteBatch = async () => {
  try {
    await message.delConfirm()
    await DictTypeApi.deleteDictTypeList(checkedIds.value)
    checkedIds.value = []
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

/** 初始化 **/
onMounted(() => {
  getList()
})
</script>
